import { type CalendarDate, parseDate } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useDebounce } from "ahooks";
import { useContext, useEffect, useState } from "react";
import { DisclosureStateContext } from "react-aria-components";
import z from "zod";

import { isAdminRole } from "@/auth/shared";
import { EditInvoiceLineItems } from "@/components/admin/edit-invoice-line-items";
import { EditInvoiceProfiles } from "@/components/admin/edit-invoice-profiles";
import { CopyButton } from "@/components/base/copy-button";
import { DatePicker } from "@/components/base/date-picker";
import {
  Disclosure,
  DisclosureGroup,
  DisclosureHeader,
  DisclosurePanel,
} from "@/components/base/disclosure";
import { Pagination } from "@/components/base/pagination";
import { title } from "@/components/base/primitives";
import { SearchField } from "@/components/base/search-field";
import { ProfileName } from "@/components/profiles/name";
import { TeamNames } from "@/components/teams/names";
import {
  getTournamentDisplay,
  getTournamentDivisionDisplay,
} from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { getDefaultTimeZone } from "@/lib/dates";

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const searchSchema = z.object({
  name: z.string().default(""),
  key: z.string().default(""),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  page: z.number().default(1),
  pageSize: z.number().default(25),
});

export const Route = createFileRoute("/admin/invoices")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [{ title: "CBVA - Invoices" }],
  }),
  loader: async ({ context: { queryClient, ...context } }) => {
    const viewer = context.viewer;

    if (!viewer || !isAdminRole(viewer.role)) {
      throw redirect({ to: "/not-found" });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const {
    name: nameParam,
    key,
    startDate,
    endDate,
    page,
    pageSize,
  } = Route.useSearch();
  const router = useRouter();
  const trpc = useTRPC();

  const [name, setName] = useState(nameParam);
  const debouncedName = useDebounce(name, { wait: 300 });

  useEffect(() => {
    router.navigate({
      to: "/admin/invoices",
      search: (search) => ({
        ...search,
        name: debouncedName || undefined,
        page: 1,
      }),
      replace: true,
    });
  }, [debouncedName, router]);

  const { data: { data: invoices, pageInfo } = {}, isLoading } = useQuery(
    trpc.invoices.list.queryOptions({
      name: nameParam,
      transactionKey: key,
      startDate,
      endDate,
      pageInfo: {
        page,
        size: pageSize,
      },
    }),
  );

  const dateFormatter = useDateFormatter({
    dateStyle: "short",
    timeZone: getDefaultTimeZone(),
  });

  const dateTimeFormatter = useDateFormatter({
    dateStyle: "short",
    timeStyle: "short",
    timeZone: getDefaultTimeZone(),
  });

  const navigate = useNavigate();

  return (
    <DefaultLayout
      classNames={{
        content: "flex flex-col space-y-8 w-full max-w-2xl px-3 py-12 mx-auto",
      }}
    >
      <section className="flex flex-col space-y-2">
        <h2
          className={title({
            size: "sm",
            class: "flex flex-row justify-between items-center",
          })}
        >
          <span>Invoices</span>
          <span className="text-sm font-normal text-gray-500">
            {pageInfo?.totalItems} total
          </span>
        </h2>

        <SearchField label="Search by name" value={name} onChange={setName} />

        <SearchField
          label="Paste Transaction Key"
          value={key}
          onChange={(value) => {
            navigate({
              replace: true,
              search: (search) => ({
                ...search,
                key: value,
              }),
            });
          }}
        />

        <div className="mb-6 grid grid-cols-2 gap-4">
          <DatePicker<CalendarDate>
            label="Start Date"
            value={startDate ? parseDate(startDate) : null}
            onChange={(value) => {
              navigate({
                replace: true,
                search: (search) => ({
                  ...search,
                  startDate: value?.toString() || undefined,
                  page: 1,
                }),
              });
            }}
          />
          <DatePicker<CalendarDate>
            label="End Date"
            value={endDate ? parseDate(endDate) : null}
            onChange={(value) => {
              navigate({
                replace: true,
                search: (search) => ({
                  ...search,
                  endDate: value?.toString() || undefined,
                  page: 1,
                }),
              });
            }}
          />
        </div>

        {isLoading ? (
          <p className="text-gray-500">Loading invoices...</p>
        ) : invoices?.length === 0 ? (
          <p className="text-gray-500">No invoices found.</p>
        ) : (
          <DisclosureGroup className="bg-white">
            {invoices?.map((invoice) => (
              <Disclosure key={invoice.id}>
                <DisclosureHeader
                  size="sm"
                  contentClassName="flex-1 flex flex-row justify-between items-center gap-4"
                >
                  <span className="text-xs text-gray-500">
                    #{invoice.id}
                    <span className="sm:hidden">
                      {invoice.createdAt && (
                        <>- {dateFormatter.format(invoice.createdAt)}</>
                      )}
                    </span>
                    <span className="hidden sm:inline">
                      {invoice.createdAt && (
                        <>- {dateTimeFormatter.format(invoice.createdAt)}</>
                      )}
                    </span>
                  </span>
                  <span className="flex-1 truncate">
                    {invoice.purchaserName || invoice.purchaserEmail}
                  </span>
                  <span className="hidden text-sm text-gray-600 sm:inline">
                    {invoice.membershipCount > 0 && (
                      <span>{invoice.membershipCount} membership(s)</span>
                    )}
                    {invoice.membershipCount > 0 &&
                      invoice.tournamentRegistrationCount > 0 &&
                      ", "}
                    {invoice.tournamentRegistrationCount > 0 && (
                      <span>
                        {invoice.tournamentRegistrationCount} registration(s)
                      </span>
                    )}
                    {(invoice.membershipCount > 0 ||
                      invoice.tournamentRegistrationCount > 0) &&
                      invoice.teamRegistrationCount > 0 &&
                      ", "}
                    {invoice.teamRegistrationCount > 0 && (
                      <span>
                        {invoice.teamRegistrationCount} team registration(s)
                      </span>
                    )}
                  </span>
                </DisclosureHeader>
                <DisclosurePanel>
                  <InvoiceDetail
                    invoiceId={invoice.id}
                    transactionKey={invoice.transactionKey}
                  />
                </DisclosurePanel>
              </Disclosure>
            ))}
          </DisclosureGroup>
        )}

        {pageInfo && pageInfo.totalPages > 1 && (
          <Pagination
            to="/admin/invoices"
            page={page}
            pageSize={pageSize}
            pageInfo={pageInfo}
          />
        )}
      </section>
    </DefaultLayout>
  );
}

function InvoiceDetail({
  invoiceId,
  transactionKey,
}: {
  invoiceId: number;
  transactionKey: string;
}) {
  const { isExpanded } = useContext(DisclosureStateContext)!;
  const trpc = useTRPC();

  const { data: invoice, isLoading } = useQuery({
    ...trpc.invoices.detail.queryOptions({ id: invoiceId }),
    enabled: isExpanded,
    staleTime: 30_000,
  });

  const dateTimeFormatter = useDateFormatter({
    dateStyle: "short",
    timeStyle: "short",
    timeZone: getDefaultTimeZone(),
  });

  if (!isExpanded) return null;

  if (isLoading || !invoice) {
    return <p className="py-2 text-sm text-gray-500">Loading details...</p>;
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="flex w-full flex-row items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">
            Transaction Key
          </span>
          <span className="flex flex-row items-center gap-x-1 text-xs">
            <span>{transactionKey}</span>
            <CopyButton size="xs" value={transactionKey} />
          </span>
        </div>
        <span className="flex flex-row gap-2">
          <EditInvoiceProfiles invoice={invoice} />
          <EditInvoiceLineItems invoice={invoice} />
        </span>
      </div>

      {invoice.refunds.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-red-600">Refunds</span>
          <ul className="space-y-1">
            {invoice.refunds.map((refund) => (
              <li
                key={refund.id}
                className="flex flex-col gap-0.5 border-l-2 border-red-300 pl-2 text-xs"
              >
                <span className="flex justify-between">
                  <span className="font-medium">
                    {moneyFormatter.format(refund.amount)} refunded
                  </span>
                  <span className="text-gray-500">
                    {moneyFormatter.format(refund.fee)} fee
                  </span>
                </span>
                <span className="flex flex-row items-center gap-x-1 text-gray-500">
                  <span>{refund.transactionKey}</span>
                  <CopyButton size="xs" value={refund.transactionKey} />
                </span>
                {refund.createdAt && (
                  <span className="text-gray-400">
                    {dateTimeFormatter.format(refund.createdAt)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {invoice.memberships.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">Memberships</span>
          <ul className="space-y-1">
            {invoice.memberships.map((membership) => (
              <li key={membership.id} className="flex justify-between">
                <ProfileName {...membership.profile} />
                <span className="text-gray-500">
                  Valid until{" "}
                  {dateTimeFormatter.format(
                    parseDate(membership.validUntil).toDate(
                      getDefaultTimeZone(),
                    ),
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {invoice.tournamentRegistrations.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">
            Tournament Registrations
          </span>
          <ul className="space-y-1">
            {invoice.tournamentRegistrations.map((registration) => (
              <li key={registration.id}>
                Team #{registration.tournamentDivisionTeamId}
              </li>
            ))}
          </ul>
        </div>
      )}
      {invoice.teamRegistrations.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">
            Team Registrations
          </span>
          <ul className="space-y-1">
            {invoice.teamRegistrations.map((teamReg) => (
              <li key={teamReg.id} className="gap-y flex flex-col">
                <span>
                  {getTournamentDisplay(teamReg.tournamentDivision.tournament)}{" "}
                  {getTournamentDivisionDisplay(teamReg.tournamentDivision)}
                </span>
                <span className="text-xs text-gray-600">
                  <TeamNames players={teamReg.team.players} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
